import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Button,
  Stack,
} from '@mui/material';
import {
  getStoredMemberships,
  persistMemberships,
  persistSession,
  selectStore,
  unlockStore,
} from '../../Redux/Auth/authApi';
import { StoreMembership } from '../../types/company';

const roleLabel = (role: string): string => {
  switch (role) {
    case 'ROLE_MANAGER':
      return '店舗管理者';
    case 'ROLE_CLERK':
      return '店員';
    default:
      return role;
  }
};

const SelectStore: React.FC = () => {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState<StoreMembership[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const stored = getStoredMemberships();
    if (stored.length === 0) {
      // 一時情報が無い（直接アクセス・リロードで失効など）→ ログインへ
      navigate('/login');
      return;
    }
    setMemberships(stored);
  }, [navigate]);

  const handleSelect = (storeId: string) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    selectStore(storeId)
      .then((auth) => {
        persistSession(auth);
        navigate(`/home?storeClerkId=${auth.clerkId}`);
      })
      .catch((err: Error) => {
        console.error('Error:', err);
        setError(
          err.message === 'STORE_LOCKED'
            ? 'この店舗は削除リクエスト中のためロックされています。ロックを解除してください。'
            : '店舗の選択に失敗しました。再度ログインしてください。',
        );
        setSubmitting(false);
      });
  };

  const handleUnlock = (storeId: string) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    unlockStore(storeId)
      .then(() => {
        // ローカルの所属一覧のロック状態を解除し、localStorage にも反映する
        // （画面更新・遷移後も解除状態が保たれるようにする）。
        setMemberships((prev) => {
          const updated = prev.map((m) => (m.storeId === storeId ? { ...m, locked: false } : m));
          persistMemberships(updated);
          return updated;
        });
      })
      .catch((err) => {
        console.error('Error:', err);
        setError('ロックの解除に失敗しました。再度ログインしてお試しください。');
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" gutterBottom align="center">
            操作する店舗を選択してください
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', my: 2 }}>
              {error}
            </Alert>
          )}

          {submitting ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ mt: 2 }}>
              {memberships.map((m) =>
                m.locked ? (
                  // 削除リクエスト中（ロック）の店舗は入室不可。解除ボタンを出す。
                  <ListItem
                    key={`${m.companyId}-${m.storeId}`}
                    sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 2, mb: 1.5, flexWrap: 'wrap', gap: 1 }}
                  >
                    <ListItemText primary={m.storeName} secondary={m.companyName} />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label="削除リクエスト中" size="small" color="warning" />
                      <Button size="small" variant="outlined" color="warning" onClick={() => handleUnlock(m.storeId)}>
                        ロック解除
                      </Button>
                    </Stack>
                  </ListItem>
                ) : (
                  <ListItemButton
                    key={`${m.companyId}-${m.storeId}`}
                    onClick={() => handleSelect(m.storeId)}
                    sx={{ border: '1px solid #eee', borderRadius: 2, mb: 1.5 }}
                  >
                    <ListItemText primary={m.storeName} secondary={m.companyName} />
                    <Chip label={roleLabel(m.role)} size="small" color="primary" variant="outlined" />
                  </ListItemButton>
                ),
              )}
            </List>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default SelectStore;
